import { CellClickedEvent, GridOptions, IRowModel, RowNode } from 'ag-grid-community';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { TimezoneService } from 'src/app/reusables/services/timezone.service';
import { MultiFactorService } from '../multi-factor.service';
import {
    DashboardDTO, GemHistoryDTOV2, Group, GroupAttribute, GroupDTO, GroupTypeDTO, ImageDTO, ImgUpdateResponse, Language, LDGroupInfo,
    PageDTO,
    Permissions, ProfileDTO, SalesPackageAssignmentWSTO, SpendingPointHistoryDTOV2, UpdateUserDTO, User, UserAdminServiceData,
    UserProfileDTO, UserRowData
} from '../user-admin.model';
import { UserAdminService } from '../user-admin.service';


@Component({
    selector: 'cv-user-admin',
    templateUrl: './user-admin.component.html',
    styleUrls: ['./user-admin.component.scss']
})
export class UserAdminComponent implements OnInit {
    public uas: UserAdminServiceData;
    public permissions: Permissions;
    public isLoading: boolean = false;
    public approvingDisapproving:boolean;
    //holds value of currently viewing page
    public pageIndex: number = 0;
    public localizationDic: Map<string, string> = new Map();

    //holds all LoginDTOs for users
    public listUsers: User[] = [];
    //holds all UserProfileDTOs for users
    public listUsersProfiles: UserProfileDTO[] = [];
    //holds all ImageDTOs for profile pics
    public listUsersImages: ImageDTO[] = [];
    //holds all profile DTOs to match on ProfileIDs
    public mapProfileDTOs: Map<number, ProfileDTO> = new Map<number, ProfileDTO>();
    //list of profileDTOs for viewing/edit
    public listProfileDTOs: ProfileDTO[] = [];
    //map of user profiles bound on ImageID for image sorting
    public mapLoginsOnImageID: Map<number, ImageDTO> = new Map();
    //holds groups for group naming
    public groups: Group[];
    // holes groupTypeDTO for naming
    public groupNames: GroupTypeDTO[] = [];
    //hold list of available languages
    public langs: Language[];

    // user image related
    public uploadingImg: boolean = false;

    //Prevent passsword reset request from being called multiple times
    public isSendingReset: boolean = false;

    // group info
    public baseGroupTypes: string[] = [];
    public levelOptions: GroupTypeDTO[] = [];
    public allLDGroups: LDGroupInfo[] = [];
    public selectedGroupLevelIndex: number = 100;

    //toggle for in edit state
    public isEditing: boolean = false;

    public isEditingG: boolean = false;
    //toggle for editable page 
    public editablePage: boolean = true;

    //If user has create and write permissions
    public hasPermissionToApprove: boolean = false;
    //If image is currently being hovered over, display text
    public isHovering: boolean = false;

    public selectedUserTimezone: string;
    public selectedUserInfo: User = new User();
    public selectedUserParentGroupName: string;
    public selectedUserParentGroupID: number;
    public selectedUserProfile: UserProfileDTO;
    public selectedUserPicturePath: string;
    public selectedUserPackages: SalesPackageAssignmentWSTO[] = [];
    public selectedUserGemHistory: GemHistoryDTOV2[];
    public selectedUserCurrencyHistory: SpendingPointHistoryDTOV2[] = [];
    public selectedUserAttributes: GroupAttribute[] = [];
    public selectedUserlang: string;

    //holds attribute groups -- Attributes Page
    public attributesGroups: string[] = [];

    //hold number of challenges won -- Gamification Page
    public selectedUserChallengeWon: number = 0;

    //hold list of dashboard dtos -- Dashboards Page
    public dashboards: DashboardDTO[];

    //hold list of pageAccess for the logged in user -- Access Page
    public pageAccess: PageDTO[];
    public pageAccessViewOnly: boolean;
    public accessTab: boolean = true;


    constructor(
        private service: UserAdminService,
        private localizationService: CVLocalizationService,
        private multifactorService: MultiFactorService,
        private timezoneService: TimezoneService,
        public gridHelperService: AgGridHelperService
    ) {
    }

    ngOnInit(): void {
        this.timezoneService.getUserTimezoneInfo();
        this.initAsync();
    }

    async initAsync() {
        this.localizationDic = await this.localizationService.GetSection("Users");
        let ProfileManagementAccess = await this.service.getPageUserAccess();

        this.updateLanguageList();

        this.permissions = await this.service.getLoginUserPermissions();

        this.hasPermissionToApprove = ProfileManagementAccess.CreateAccess || ProfileManagementAccess.WriteAccess;

        this.uas = this.service.dataObj;

        this.getUserDetails();

        this.checkUserAccess();
    };

    async updateLanguageList() {
        this.langs = await this.service.getLanguages();
        this.langs.forEach((el) => {
            if (el.Value == "NoneSet") {
                var defaultLanguage = "";
                this.langs.forEach((atom) => {
                    if (atom.Key == 10086) defaultLanguage = atom.Value;
                })
                el.Value = this.localizationDic.get("Company_Default") + " (" + this.localizationDic.get(defaultLanguage) + ")";
            }
        });
        this.langs.pop();
    }

    createOrWriteAccess(): boolean {
        return this.permissions?.CreateAccess || this.permissions?.WriteAccess;
    }

    /* gets all users for the company and returns LoginDTOs/UserProfileDTOs/ImageDTOs
        populates all necessary fields to view users' list */
    async getUserDetails() {
        this.isLoading = true;
        let failed: boolean = false;
        let userList: User[];
        let promises: Promise<any>[] = [];

        promises.push(this.service.loadUsersList().then((response) => { userList = response; }));
        promises.push(this.service.loadUsersProfilesList().then((response) => { this.listUsersProfiles = response; },
            () => { alert("Please impersonate one valid company first."); }));
        promises.push(this.service.getAllProfileDTOs().then((response) => { this.listProfileDTOs = response; }));
        promises.push(this.service.loadUsersProfilesPicturesList().then((response) => { this.listUsersImages = response; }));
        promises.push(this.service.loadUserGroupTypes().then((response) => { this.groupNames = response; }));

        await Promise.all(promises).catch(() => { failed = true; });
        if (failed) {
            return;
        }

        this.getBaseGroupType();

        this.getAllProfileIDNames(userList);
        this.orderProfilePictures(userList);

        //assign RelatedGroupLevelName
        for (let i = 0; i < userList.length; ++i) {
            this.groupNames.forEach((group) => {
                if (group.GroupLevel == userList[i].RelatedGroupLevel && group.BaseGroupType == userList[i].RelatedGroupType) {
                    userList[i].RelatedGroupLevelName = group.GroupTypeName;
                }
            })
        }

        let ldpromises: Promise<LDGroupInfo>[] = [];
        this.groupNames.forEach((group) => {
            ldpromises.push(this.service.GetAllLDGroupInfo(group.BaseGroupType, group.GroupLevel, group.GroupTypeName)
                .then((response) => {
                    return response;
                }, (reason) => {
                    return { Key: "", Value: [] };
                }
                ));
        });

        let results: LDGroupInfo[] = await Promise.all(ldpromises)
        this.allLDGroups = results.filter(f => f.Key != "" || f.Value.length > 0);

        for (let i = 0; i < userList.length; ++i) {
            let user: User = userList[i];

            user.ParentGroupName = "N/A";
            this.allLDGroups.forEach((LDGroup) => {
                if ((LDGroup.Value[0] != null && LDGroup.Value[0].GroupLevel == user.RelatedGroupLevel + 1
                    && LDGroup.Value[0].BaseGroupType == user.RelatedGroupType)) {
                    user.ParentGroupID =
                        user.RelatedGroupLevel == 1
                            ? user.Group2ID
                            : user.RelatedGroupLevel == 2
                                ? user.Group3ID
                                : user.RelatedGroupLevel == 3
                                    ? user.Group4ID
                                    : user.RelatedGroupLevel == 4
                                        ? user.Group5ID
                                        : 0;

                    for (var idx = 0; idx < LDGroup.Value.length; idx++) {
                        if (LDGroup.Value[idx].GroupID == user.ParentGroupID) {
                            user.ParentGroupName = LDGroup.Value[idx].GroupName;
                            break;
                        }
                    }
                }
            });
        }

        this.listUsers = userList;

        this.isLoading = false;
    }

    // get basegrouptypes
    getBaseGroupType() {
        this.service.loadBaseGroupTypes().then((response) => {
            this.baseGroupTypes = [];
            response.forEach((el) => {
                if (el == 'Employee' || el == 'Recording') {
                    this.baseGroupTypes.push(el);
                }
            });
        });
    }

    //organizes pictures to the appropriate user row
    orderProfilePictures(userList: User[]) {
        var map: Map<number, ImageDTO> = new Map();
        for (var i = 0; i < this.listUsersImages.length; ++i) { //load each profile into a map
            if (this.listUsersImages[i].ImageID !== undefined && map.get(this.listUsersImages[i].ImageID) === undefined) //image exists and
                map.set(this.listUsersImages[i].ImageID, this.listUsersImages[i]); //given ImageID, return Image DTO
        }
        for (var j = 0; j < this.listUsersProfiles.length; ++j) { //match ImageID to profile and then put in master map of ImageID <-> LoginID
            if (map.get(this.listUsersProfiles[j].ImageID) !== undefined) {
                this.mapLoginsOnImageID.set(this.listUsersProfiles[j].LoginID, map.get(this.listUsersProfiles[j].ImageID)); //loginID returns ImageDTO
            }
        }
        for (var k = 0; k < userList.length; ++k) {
            if (this.mapLoginsOnImageID.get(userList[k].LoginID) !== undefined)
                userList[k].ImageID = this.mapLoginsOnImageID.get(userList[k].LoginID).ImageID;
        }
    };

    //get all profileDTOs to match to ProfileIDs
    getAllProfileIDNames(userList: User[]) {
        for (var i = 0; i < this.listProfileDTOs.length; ++i) {
            if (!this.mapProfileDTOs.has(this.listProfileDTOs[i].ProfileID))
                this.mapProfileDTOs.set(this.listProfileDTOs[i].ProfileID, this.listProfileDTOs[i]);
        }
        for (var j = 0; j < userList.length; ++j) {
            if (this.mapProfileDTOs.has(userList[j].ProfileID))
                userList[j].ProfileName = this.mapProfileDTOs.get(userList[j].ProfileID).ProfileName;
        }
    };

    //loop through users list and assign names based on group level
    getGroupNames(userList: User[]) {
        for (var i = 0; i < userList.length; ++i) {
            for (var j = 0; j < this.groups.length; ++j) {
                if (userList[i].RelatedGroupLevel === this.groups[j].GroupLevel) {
                    userList[i].RelatedGroupLevelName = this.groups[j].GroupTypeName;
                    break;
                }
            }
        }
    };

    //reset page settings between views
    async changePageIndex(index: number) {
        if (this.isEditingG) {
            return;
        }

        this.isEditing = false;
        this.isEditingG = false;
        this.pageIndex = index;
        this.levelOptions = [];

        this.editablePage = index === 3 ? false : true;
        index = index === 0 ? 0 : index - 1; //one more page than index

        //load info based on page
        if (this.pageIndex === 1) { // details page
            this.getBaseGroupType();
            if (this.selectedUserInfo.TimeZoneInfoName == "Not Set")
                this.selectedUserTimezone = this.localizationDic.get("Default");
            else
                this.selectedUserTimezone = this.selectedUserInfo.TimeZoneInfoName;

            if (!this.permissions["CreateAccess"] && !this.permissions["WriteAccess"]) {
                $(() => {
                    $(".profilePic").css("cursor", "default");
                })
            }

            if (this.langs == null) {
                this.service.getLanguages().then((result) => {
                    this.langs = result;
                    this.langs.forEach((el) => {
                        if (el.Value == "NoneSet") {
                            var defaultLanguage = "";
                            this.langs.forEach((atom) => {
                                if (atom.Key == 10086) defaultLanguage = atom.Value;
                            })
                            el.Value = this.localizationDic.get("Company_Default") + " (" + this.localizationDic.get(defaultLanguage) + ")";
                        }
                    });
                    this.selectedUserlang = this.langs[this.selectedUserInfo.LocalizationSetting].Value;

                    this.langs.pop();
                })
            }
            else {
                this.selectedUserlang = this.langs[this.selectedUserInfo.LocalizationSetting] ? this.langs[this.selectedUserInfo.LocalizationSetting].Value : this.langs[0].Value;
            }
        }
        else if (this.pageIndex === 2) { //attributes page            
            this.service.getAgentAttributesByGroup(this.selectedUserInfo.RelatedGroupID).then((response) => {
                this.selectedUserAttributes = response.attributes;
                response.attributes.forEach((el) => {
                    if (this.attributesGroups.indexOf(el.AttributeFolder) == -1) {
                        this.attributesGroups.push(el.AttributeFolder);
                    }
                });
            });
        }
        else if (this.pageIndex === 3) { //permissions page
            if (!this.permissions["CreateAccess"] && !this.permissions["WriteAccess"]) {
                $(() => {
                    $(".permissionsGroupBorder").css("cursor", "default");
                })
            }
        }
        else if (this.pageIndex === 4) { //gamification page
            this.service.getChallengeIWonData(this.selectedUserInfo.LoginID).then((response) => {
                this.selectedUserChallengeWon = response;
            });
            this.updateUserCurrencyHistory();
            this.updateUserGemHistory();
        }
        else if (this.pageIndex === 5) { //packages page
            this.service.getUserPackages(this.selectedUserInfo.LoginID).then((response) => {
                this.selectedUserPackages = response;
                //adding Localization for user packages
                this.selectedUserPackages.forEach((e1) => {
                    switch (e1.PackageID) {
                        case 1: e1.PackageName = this.localizationDic.get("Package_General"); break;
                        case 2: e1.PackageName = this.localizationDic.get("Package_Messaging"); break;
                        case 3: e1.PackageName = this.localizationDic.get("Package_Game"); break;
                        case 4: e1.PackageName = this.localizationDic.get("Package_Coaching"); break;
                        case 5: e1.PackageName = this.localizationDic.get("Package_QA"); break;
                        case 6: e1.PackageName = this.localizationDic.get("Package_Dashboard"); break;
                        case 7: e1.PackageName = this.localizationDic.get("Package_Wallboard"); break;
                        default: e1.PackageName = this.localizationDic.get("Package_Not_Assigned"); break;
                    }
                });
            });
        }
        else if (this.pageIndex === 6) { // Dashboards tab
            this.dashboards = null;
            this.dashboards = await this.service.loadDashboards(this.selectedUserInfo.LoginID);
        }
        else if (this.pageIndex === 7){

        }
        else if (this.pageIndex === 0) {
            this.selectedUserInfo = new User();
            this.selectedUserProfile = null;
            this.service.dataObj.selectedUserName = "";
        }
    };

    //send password reset request
    resetPassword(loginID) {
        if (this.isSendingReset)
            return;
        this.isSendingReset = true;
        this.service.resetPassword(loginID).then(() => {
            this.selectedUserInfo.AccountLocked = false;
        }).catch((response) => {
            showErrorMessage(response);
        }).finally(() => {
            this.isSendingReset = false;
        });
    };

    switchUserProfileActive() {
        if (this.selectedUserInfo.LoginID <= 0) {
            return;
        }

        this.service.switchUserProfileActive(this.selectedUserInfo.LoginID, this.selectedUserInfo.Active).then((resp) => {
            this.selectedUserInfo.Active = resp;
            this.getUserDetails();
        });
    }

    impersonateUser() {
        if (this.permissions.ImpersonateAccess) {
            this.service.impersonateUser(this.selectedUserInfo.LoginID).then((response) => {
                window.location.replace("../Dashboard/DashboardNew.aspx");
            });
        }
    }

    backEvent() {
        this.isEditing = false;
        this.isEditingG = false;
        this.changePageIndex(0);
    };

    //returns all necessary user information for displaying on the Details page
    getAllUserInfo(_loginID: number) {
        var login: User = this.listUsers.find((l) => { return l.LoginID === _loginID; });
        if (login) {
            this.selectedUserInfo = login;

            // initialize this.groupNames
            this.levelOptions = []
            if (this.selectedUserInfo.RelatedGroupTypeName == "Employee") {
                this.groupNames.forEach((element) => {
                    if (element.BaseGroupType == 0) {
                        this.levelOptions.push(element);
                    }
                });
            }
            else if (this.selectedUserInfo.RelatedGroupTypeName == "Recording") {
                this.groupNames.forEach((element) => {
                    if (element.BaseGroupType == 1) {
                        this.levelOptions.push(element);
                    }
                });
            }
            this.changePageIndex(1);
        }

        var profile = this.listUsersProfiles.find((p) => { return p.LoginID === _loginID; });
        if (profile) {
            this.selectedUserProfile = profile;
            this.getUserImagePath(this.selectedUserProfile.LoginID);
        }

        this.uas.selectedUserName = login && login.LoginName || "";
    }

    getUserImagePath(_loginID: number) {
        var img = this.mapLoginsOnImageID.get(_loginID);
        //check if null, some users do not have a picture attached
        if (img === undefined)
            this.selectedUserPicturePath = "";
        else
            this.selectedUserPicturePath = this.selectedUserProfile.ImageID == 0 ? '../Admin/ImageResizer.aspx?imgid=-15&w=auto&h=105' : '../Admin/ImageResizer.aspx?imgid=' + this.selectedUserProfile.ImageID + '&w=auto&h=150';
    }

    //#region Image Upload

    async updateUserImage(img: string) {
        if (!img || img == "") {
            return;
        }

        let response: ImgUpdateResponse = await this.service.updateUserImage(this.selectedUserInfo.LoginID, img);
        for (let i = 0; i < this.listUsers.length; ++i) {
            if (this.listUsers[i].LoginID == this.selectedUserInfo.LoginID) {
                this.listUsers[i].ImageID = response.ImageID;
                break;
            }
        }
        this.selectedUserProfile.ImageID = response.ImageID;
        this.getUserDetails();
        this.uploadingImg = false;
    }

    imageCancel() {
        this.uploadingImg = false;
        $("#profilePic_new").css("background-image", "url('../App_Themes/Standard/Images/CoreTheme/Profile/default_profile.svg')")
    };

    //#endregion Image Upload

    //Gamification 
    updateUserCurrencyHistory() {
        this.service.getCurrencyHistory(this.selectedUserInfo.LoginID).then((response) => {
            this.selectedUserCurrencyHistory = response;

            if (this.selectedUserCurrencyHistory[0]) {
                this.selectedUserCurrencyHistory[0].data.HistoryValue = this.selectedUserProfile.AvailableSpendingPoints;
            }
            for (var i = 0; i < this.selectedUserCurrencyHistory.length; i++) {
                var temp = this.selectedUserCurrencyHistory[i].data.CurrentPointsChange;
                this.selectedUserCurrencyHistory[i].data.CurrentPointsChangePov = (temp <= 0) ? '--' : temp.toString();
                this.selectedUserCurrencyHistory[i].data.CurrentPointsChangeNeg = (temp >= 0) ? '--' : temp.toString();

                if (i >= 1) {
                    this.selectedUserCurrencyHistory[i].data.HistoryValue = this.selectedUserCurrencyHistory[i - 1].data.HistoryValue
                        - this.selectedUserCurrencyHistory[i - 1].data.CurrentPointsChange;
                }

                var tmp = this.selectedUserCurrencyHistory[i].GivenBy.replace('.', ' ');
                if (tmp != "") {
                    var tmpList = tmp.split(' ').map((word) => {
                        return word[0].toUpperCase() + word.substr(1);
                    }).join(' ');

                    this.selectedUserCurrencyHistory[i].GivenBy = tmpList;
                }
            }
        });
    };

    updateUserGemHistory() {
        this.service.getGemHistory(this.selectedUserInfo.LoginID).then((response) => {
            this.selectedUserGemHistory = response;
            for (var i = 0; i < this.selectedUserGemHistory.length; i++) {
                var temp = this.selectedUserGemHistory[i].data.CurrentGemsChange;
                this.selectedUserGemHistory[i].data.CurrentGemsChangePov = (temp <= 0) ? '--' : temp.toString();
                this.selectedUserGemHistory[i].data.CurrentGemsChangeNeg = (temp >= 0) ? '--' : temp.toString();
            }
        });
    }

    async approveProfilePicture (loginId) {
        const profile = this.selectedUserProfile;
        this.approvingDisapproving = true;
        const response = await this.service.approveProfilePicture(loginId);
        profile.isApproved = true;
        this.approvingDisapproving = false;

        this.service.userPictureApprovedDenied.next(profile);
    };

    async denyProfilePicture (loginId) {
        const profile = this.selectedUserProfile;
        this.approvingDisapproving = true;
        const response = await this.service.denyProfilePicture(loginId);
        profile.isApproved = true;
        profile.ImageID = undefined;
        this.approvingDisapproving = false;

        this.service.userPictureApprovedDenied.next(profile);
    };

    //This function is to load in Logged In user page access. Then determine if the user is going to have access to the access tab
    //They can either have full access, no access, or view access. I need to check the Roles page and the Users page
    //Roles pageid = 2151 and 2003 Users pageID = 2145, 2002
    public async checkUserAccess(){
        let loggedInUserProfile = await this.service.getLoggedInProfile();
        this.pageAccess = await this.service.getPageAccessForLoggedInUser(loggedInUserProfile);

        //Here I have to check their page access
        for(var page in this.pageAccess ){
            if(this.pageAccess[page].PageID == 2145 || this.pageAccess[page].PageID == 2002){
                if (this.pageAccess[page].CreateAvailable || this.pageAccess[page].WriteAvailable) {
                    this.pageAccessViewOnly = false;
                    break;
                } else if(this.pageAccess[page].ReadAvailable) {
                    this.pageAccessViewOnly = true;
                    break;
                }
                else {
                    //remove access tab
                    this.accessTab = false;
                    break;
                }
            }
        }
    }

    public updateUser(user: User) {
        this.selectedUserInfo = user;
    }
}
